import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fieldClass, labelClass } from '../styles';
import { searchCountries } from './api';

interface Props {
  label?: string;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** When set, selection is limited to these names (no country search). */
  allowedOptions?: string[];
  helper?: string;
}

const MarketMultiSelect: React.FC<Props> = ({
  label = 'Markets',
  value,
  onChange,
  disabled,
  allowedOptions,
  helper,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ id: number; name: string }>>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (allowedOptions) return;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void searchCountries(query.trim())
        .then((rows) => {
          if (!cancelled) setResults(rows.slice(0, 12));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, allowedOptions]);

  const add = (name: string) => {
    if (!name || value.includes(name)) return;
    onChange([...value, name]);
    setQuery('');
    setOpen(false);
  };

  const remove = (name: string) => {
    onChange(value.filter((v) => v !== name));
  };

  const allowedFiltered = (allowedOptions || []).filter(
    (name) =>
      !value.includes(name) &&
      (!query.trim() || name.toLowerCase().includes(query.trim().toLowerCase()))
  );
  const countryFiltered = results.filter((r) => !value.includes(r.name));

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((name) => (
          <span
            key={name}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-[#F3F3F3] text-[12px] text-[#0C0B0B]"
          >
            {name}
            {!disabled && (
              <button type="button" onClick={() => remove(name)} aria-label={`Remove ${name}`}>
                <X className="w-3 h-3 text-[#8A8A8A]" />
              </button>
            )}
          </span>
        ))}
        {value.length === 0 && (
          <span className="text-[12px] text-[#8A8A8A]">No markets selected</span>
        )}
      </div>
      {!disabled && (
        <div className="relative">
          <input
            className={fieldClass}
            value={query}
            placeholder={allowedOptions ? 'Filter project markets…' : 'Search countries…'}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
          {open && (query.trim() || Boolean(allowedOptions)) && (
            <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-[#E8E8E8] bg-white shadow-lg">
              {allowedOptions
                ? allowedFiltered.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F5]"
                      onClick={() => add(name)}
                    >
                      {name}
                    </button>
                  ))
                : countryFiltered.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F5]"
                      onClick={() => add(row.name)}
                    >
                      {row.name}
                    </button>
                  ))}
              {(allowedOptions ? allowedFiltered.length === 0 : countryFiltered.length === 0) && (
                <div className="px-3 py-2 text-sm text-[#8A8A8A]">No matches</div>
              )}
            </div>
          )}
        </div>
      )}
      {helper && <p className="mt-1 text-[12px] text-[#8A8A8A]">{helper}</p>}
    </div>
  );
};

export default MarketMultiSelect;
